import * as React from 'react';
import moment from 'moment';
import { Field, Form, InjectedFormProps } from 'redux-form';
import { connect } from 'react-redux';

import { reduxFormWrapper } from 'app/components/common/form';
import { Typography } from 'app/components/domain/typography';
import { FormCheckbox, FormDatePicker } from 'app/components/common/inputs';
import { SubmitButton } from 'app/components/common/buttons';
import { i18n } from 'app/i18n';
import { validators } from 'app/validation';
import { UpdateActivationDateFormModel } from 'app/page/orders/update-activation-date/update-activation-date-form-model';
import { UPDATE_ACTIVATION_DATE_FORM_FIELDS } from 'app/page/orders/update-activation-date/update-activation-date-form-fields';
import { RootState } from 'app/redux';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { OrderUtils } from 'app/page/orders/utils/order-utils';

import styles from './update-activation-date-form.module.scss';

const requiredValidation = validators.required();

interface StateProps {
    selectedBillingAccountId?: string;
    billingAccounts: BillingAccount[];
}

type Props = StateProps & InjectedFormProps<UpdateActivationDateFormModel>;

class UpdateActivationDateFormComponent extends React.Component<Props> {
    public render(): React.ReactNode {
        const { form, submitting, handleSubmit } = this.props;

        return (
            <Form className={styles.updateActivationDateForm} name={form} onSubmit={handleSubmit}>
                <Typography variant="heading" component="h1" className={styles.title}>
                    {i18n.updateActivationDate.title}
                </Typography>
                <Typography variant="subtitle3" className={styles.activationDate}>
                    {i18n.updateActivationDate.form.field.activationDate.title}
                </Typography>
                <Field
                    label={i18n.updateActivationDate.form.field.activationDate.label}
                    name={UPDATE_ACTIVATION_DATE_FORM_FIELDS.activationDate}
                    component={FormDatePicker}
                    required={true}
                    validate={requiredValidation}
                    disabledDate={this.isActivationDateDisabled}
                />
                <Typography variant="body1" component="p" className={styles.activationDate}>
                    {i18n.updateActivationDate.form.field.activationDate.note}
                </Typography>
                <Field
                    label={i18n.common.form.field.rightOfWithdrawalIsAgreed.label}
                    tooltip={{
                        title: i18n.common.form.field.rightOfWithdrawalIsAgreed.label,
                        text: i18n.common.form.field.rightOfWithdrawalIsAgreed.tooltip.text,
                    }}
                    name={UPDATE_ACTIVATION_DATE_FORM_FIELDS.rightOfWithdrawalIsAgreed}
                    component={FormCheckbox}
                    required={true}
                    validate={requiredValidation}
                >
                    {i18n.common.form.field.rightOfWithdrawalIsAgreed.content}
                </Field>
                <SubmitButton submitting={submitting} size="small" className={styles.button}>
                    {i18n.updateActivationDate.button.label}
                </SubmitButton>
            </Form>
        );
    }

    private readonly isActivationDateDisabled = (date: moment.Moment): boolean => {
        const { billingAccounts, selectedBillingAccountId } = this.props;

        if (!selectedBillingAccountId) {
            throw new Error('Billing account must be selected.');
        }

        /**
         * A lead time of 0 days means you can order today
         * A lead time of more than 0 days means you can order X days after tomorrow
         */
        const leadTime = OrderUtils.getLeadTime(billingAccounts, selectedBillingAccountId);
        const daysToAdd = leadTime > 0 ? leadTime + 1 : 0;

        return date.startOf('day').isBefore(moment().startOf('day').add(daysToAdd, 'day'));
    };
}

const mapStateToProps = (state: RootState): StateProps => {
    const {
        orders: { selectedBillingAccountId, billingAccounts },
    } = state;

    return {
        selectedBillingAccountId,
        billingAccounts,
    };
};

const connectedUpdateActivationDateForm = connect(mapStateToProps)(UpdateActivationDateFormComponent);

const UpdateActivationDateForm = reduxFormWrapper<UpdateActivationDateFormModel>({})(connectedUpdateActivationDateForm);

export { UpdateActivationDateForm };
